import { getSupabaseClient } from './supabase';
import { trackEvent } from './analytics/posthog';
import type { Team, TeamMembership, CoachNote } from '@shared/schema';

/**
 * Make an authenticated API request with the user's JWT token
 */
async function authenticatedRequest(
  method: string,
  url: string,
  data?: unknown
): Promise<Response> {
  const supabase = await getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    try { trackEvent('auth_session_expired', { page_source: window.location.pathname }); } catch {}
    throw new Error('Not authenticated');
  }

  // If token is expired or about to expire (within 30s), refresh it
  let accessToken = session.access_token;
  const expiresAt = session.expires_at; // Unix timestamp in seconds
  if (expiresAt && expiresAt < Math.floor(Date.now() / 1000) + 30) {
    const { data: refreshData } = await supabase.auth.refreshSession();
    if (refreshData.session?.access_token) {
      accessToken = refreshData.session.access_token;
    } else {
      try { trackEvent('auth_session_expired', { page_source: window.location.pathname, reason: 'refresh_failed' }); } catch {}
      throw new Error('Not authenticated');
    }
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${accessToken}`,
  };

  if (data) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    let errorMessage = res.statusText;
    try {
      const errorBody = await res.text();
      if (errorBody) {
        try {
          const errorJson = JSON.parse(errorBody);
          errorMessage = errorJson.message || errorBody;
        } catch {
          errorMessage = errorBody;
        }
      }
    } catch {
      errorMessage = res.statusText;
    }
    throw new Error(`${res.status}: ${errorMessage}`);
  }

  return res;
}

// ============================================================
// Types
// ============================================================

export interface TeamWithCoach {
  hasTeam: boolean;
  team: {
    id: number;
    name: string;
    teamCredits: number;
    maxMembers: number;
  } | null;
  coach: {
    id: number;
    name: string | null;
    email: string;
    profilePicture: string | null;
  } | null;
  membership: {
    id: number;
    joinedAt: string;
    canUseTeamCredits: boolean;
  } | null;
}

export interface JoinTeamResponse {
  success: boolean;
  team: {
    id: number;
    name: string;
    teamCredits: number;
  };
  coach: {
    id: number;
    name: string | null;
  } | null;
  membership: TeamMembership;
}

export interface PlayerCreditsResponse {
  personal: {
    credits: number;
    planCredits: number;
    bonusCredits: number;
    planTier: string;
    nextCreditsResetAt: string | null;
  };
  team: {
    teamName: string | null;
    teamCredits: number | null;
    canUseTeamCredits: boolean;
  } | null;
}

// ============================================================
// Player Team API
// ============================================================

export const playerTeamApi = {
  /**
   * Get the current team the player belongs to
   */
  getCurrentTeam: async (): Promise<TeamWithCoach> => {
    const res = await authenticatedRequest('GET', '/api/player/team');
    return res.json();
  },

  /**
   * Join a team using an invite code
   */
  joinTeam: async (inviteCode: string): Promise<JoinTeamResponse> => {
    const res = await authenticatedRequest('POST', '/api/player/team/join', { inviteCode });
    return res.json();
  },

  /**
   * Leave the current team
   */
  leaveTeam: async (): Promise<{ success: boolean; message: string }> => {
    const res = await authenticatedRequest('POST', '/api/player/team/leave');
    return res.json();
  },

  /**
   * Get coach notes on the player's content (visible to player per requirements)
   */
  getMyCoachNotes: async (): Promise<CoachNote[]> => {
    const res = await authenticatedRequest('GET', '/api/player/team/coach-notes');
    return res.json();
  },

  /**
   * Get player's credit information (both team and personal)
   */
  getCredits: async (): Promise<PlayerCreditsResponse> => {
    const res = await authenticatedRequest('GET', '/api/player/team/credits');
    return res.json();
  },
};
